'use client';

import { getDebugger } from './debug-performance';

interface PerformanceMark {
  name: string;
  startTime: number;
}

class PerformanceMonitor {
  private marks: Map<string, PerformanceMark> = new Map();
  private pageType: string;
  private pageName: string;
  private componentRenderStart: number = 0;

  constructor(pageType: string, pageName: string) {
    this.pageType = pageType;
    this.pageName = pageName;
    this.componentRenderStart = performance.now();
  }

  mark(name: string) {
    this.marks.set(name, {
      name,
      startTime: performance.now()
    });
  }

  measure(name: string, startMark: string, details?: string) {
    const start = this.marks.get(startMark);
    if (!start) return;

    const duration = performance.now() - start.startTime;
    const debug = getDebugger(this.pageType);
    debug.log(name, details || `${name} completed in ${duration.toFixed(2)}ms`);
  }

  trackComponentMount() {
    const debug = getDebugger(this.pageType);
    const mountTime = performance.now() - this.componentRenderStart;
    debug.log('COMPONENT_MOUNT', `Component mounted in ${mountTime.toFixed(2)}ms`);
  }

  trackDataFetch(dataType: string, startTime: number) {
    const debug = getDebugger(this.pageType);
    const fetchTime = performance.now() - startTime;
    debug.log(`DATA_FETCH_${dataType.toUpperCase()}`, `${dataType} data fetched in ${fetchTime.toFixed(2)}ms`);
  }

  trackImageLoad(imageSrc: string, startTime: number) {
    const debug = getDebugger(this.pageType);
    const loadTime = performance.now() - startTime;
    debug.log('IMAGE_LOAD', `Image loaded in ${loadTime.toFixed(2)}ms`);
  }

  trackInteraction(interactionType: string, startTime: number) {
    const debug = getDebugger(this.pageType);
    const interactionTime = performance.now() - startTime;
    debug.log(`USER_${interactionType.toUpperCase()}`, `${interactionType} completed in ${interactionTime.toFixed(2)}ms`);
  }

  // Track real navigation timing
  trackRealNavigation() {
    const debug = getDebugger(this.pageType);
    
    // Track time to interactive (approximation)
    const interactiveTime = performance.now() - this.componentRenderStart;
    debug.log('TIME_TO_INTERACTIVE', `Page interactive in ${interactiveTime.toFixed(2)}ms`);

    // Use PerformanceObserver for more accurate metrics
    if ('PerformanceObserver' in window) {
      try {
        // Observe Largest Contentful Paint
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          debug.log('LARGEST_CONTENTFUL_PAINT', `LCP at ${lastEntry.startTime.toFixed(2)}ms`);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });

        // Observe First Input Delay
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry: any) => {
            const delay = entry.processingStart - entry.startTime;
            debug.log('FIRST_INPUT_DELAY', `FID: ${delay.toFixed(2)}ms`);
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });

        // Clean up after a delay
        setTimeout(() => {
          lcpObserver.disconnect();
          fidObserver.disconnect();
        }, 10000);
      } catch (_e) {
        // Silently fail if observers not supported
      }
    }
  }
}

export function createPerformanceMonitor(pageType: string, pageName: string): PerformanceMonitor {
  return new PerformanceMonitor(pageType, pageName);
}